import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { IonNav, AlertController } from '@ionic/angular';
import { CommonService } from 'src/app/services/common.service';
import { CaseService } from 'src/app/services/case.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-ezidebit-ach',
  templateUrl: './ezidebit-ach.page.html',
  styleUrls: ['./ezidebit-ach.page.scss'],
})
export class EzidebitAchPage implements OnInit {
  @Input() caseId;
  @Input() responseData;
  @Input() debtorDetails;
  @Input() totalOutstandingWithSymbol;
  @Input() caseDetails;
  @Input() caseData;
  @Input() planType = '';
  @Input() selectedPlanData;
  @Input() isPaymentProposal;

  achForm: FormGroup;
  amount = '';
  surcharge_amount = 0;
  company = '';
  message = '';
  saveBtnDisabled = false;
  paymentGatewayTemplate;
  fieldLabels: any = {};
  isRecurringPayment = false;
  frequency;
  firstPaymentDate;
  outstanding;
  paymentFrequencyOption = '';
  caseList = [];
  showOption = false;
  minPostDate = new Date().toISOString().split('T')[0];

  constructor(
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private caseService: CaseService,
    private authService: AuthService,
    private ionNav: IonNav,
    private alertController: AlertController,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.debtorDetails = this.debtorDetails || this.authService.debtorDetails;
    this.isRecurringPayment = !!this.planType;
    if (this.isRecurringPayment) {
      const paymentData = this.caseService.paymentData;
      this.amount = paymentData.amount;
      this.frequency = paymentData.freq;
      this.firstPaymentDate = paymentData.date;
      this.outstanding = paymentData.total;
      this.caseList = paymentData.caseList;
      this.paymentFrequencyOption = paymentData.paymentFrequencyOption;
      if (this.planType === 'group_plan') {
        this.caseId = this.caseData[0].id;
      } else {
        this.caseId = paymentData.caseId;
      }
    } else {
      this.amount = parseFloat(this.responseData?.net_amount || 0).toFixed(2);
      this.surcharge_amount = this.responseData?.surcharge_amount;
      this.company = this.responseData?.company;
    }

    const portalSettings = JSON.parse(localStorage.getItem('portal_settings'));
    const lang = localStorage.getItem('language');
    this.message = (lang !== 'en')
      ? (portalSettings?.['payment_type_message_' + lang] || portalSettings?.payment_type_message)
      : portalSettings?.payment_type_message;

    if (portalSettings?.fieldLabels) {
      this.fieldLabels = portalSettings.fieldLabels;
    }
    if (portalSettings?.gateway_templates?.ezidebit?.ach_non_recurring_payment) {
      this.paymentGatewayTemplate = portalSettings.gateway_templates.ezidebit.ach_non_recurring_payment;
    }

    this.initForm();
  }

  toggleOptions(value: boolean) {
    this.showOption = value === false;
  }

  initForm() {
    this.achForm = this.formBuilder.group({
      amount: [{ value: this.amount || '0', disabled: true }, [Validators.required]],
      account_name: [
        this.debtorDetails?.debtor_name || '',
        [Validators.required],
      ],
      bsb: [
        '',
        [Validators.required, Validators.pattern('^[0-9]{3}-?[0-9]{3}$')],
      ],
      account_number: [
        '',
        [Validators.required, Validators.pattern('^[0-9]{1,9}$')],
      ],
      debtor_name: [
        this.debtorDetails?.debtor_name || '',
        [Validators.required],
      ],
      debtor_email: [
        this.debtorDetails?.debtor_email || '',
        [Validators.required, Validators.email],
      ],
      address_ln1: [
        this.debtorDetails?.addresses?.[0]?.address_ln1 || '',
        [Validators.required],
      ],
      address_postcode: [
        this.debtorDetails?.addresses?.[0]?.address_postcode || '',
        [Validators.required],
      ],
      post_date: [this.minPostDate, [Validators.required]],
      acceptTerms: [false],
    });
  }

  async save() {
    this.achForm.markAllAsTouched();
    if (!this.achForm.valid) {
      return;
    }

    if (this.paymentGatewayTemplate?.above_proceed && !this.achForm.get('acceptTerms').value) {
      this.showAlert(this.translate.instant('PAYMENT.AGREE_TO_TERMS'));
      return;
    }

    if (this.responseData?.confirmMessage) {
      const alert = await this.alertController.create({
        header: this.translate.instant('PAYMENT.CONFIRM_AGREEMENT'),
        message: `<ion-item>
                    <ion-label position="stacked">${this.translate.instant('PAYMENT.ENTER_NAME')}</ion-label>
                    <ion-input name="name" type="text" placeholder="${this.translate.instant('PAYMENT.ENTER_NAME')}" required></ion-input>
                  </ion-item>
                  <ion-item lines="none">
                    <ion-checkbox name="agreement" slot="start" value="agree"></ion-checkbox>
                    <ion-label>${this.translate.instant('PAYMENT.AGREED')}</ion-label>
                  </ion-item>`,
        cssClass: 'custom-alert-class',
        buttons: [
          {
            text: this.translate.instant('COMMON.CANCEL'),
            role: 'cancel',
            handler: () => {},
          },
          {
            text: 'OK',
            handler: () => {
              const nameInput       = document.querySelector('ion-input[name="name"]') as HTMLIonInputElement;
              const agreementCheck  = document.querySelector('ion-checkbox[name="agreement"]') as HTMLIonCheckboxElement;
              const name            = nameInput?.value;
              const agreement       = agreementCheck?.checked;
              if (name && agreement) {
                this.confirmPayment();
              } else {
                this.showAlert(!name
                  ? this.translate.instant('PAYMENT.ENTER_NAME')
                  : this.translate.instant('PAYMENT.AGREE_TO_TERMS'));
                return false;
              }
            },
          },
        ],
      });
      await alert.present();
    } else {
      this.confirmPayment();
    }
  }

  confirmPayment() {
    this.saveBtnDisabled = true;
    this.commonService.showLoader();

    const [first_name, last_name] = this.achForm.value.debtor_name.split(/ (.+)/);

    if (this.isRecurringPayment) {
      const start = this.paymentFrequencyOption === 'month'
        ? moment(this.firstPaymentDate).format('YYYY-MM-DD')
        : this.firstPaymentDate.split('T')[0];

      const recurringPayload = {
        amount: this.amount ? this.amount : this.outstanding,
        first_amount: 0,
        freq: this.frequency,
        start,
        ref_amount: this.outstanding,
        surcharge_amount: 0,
        debtor_name: this.achForm.value.debtor_name,
        debtor_email: this.achForm.value.debtor_email,
        account_name: this.achForm.value.account_name,
        bsb: this.achForm.value.bsb,
        account_number: this.achForm.value.account_number,
        payment_method: 'ezidebit',
        debtor_id: this.debtorDetails.debtor_id,
        address_ln1: this.achForm.value.address_ln1,
        address_ln2: '',
        town: '',
        address_ln3: '',
        address_postcode: this.achForm.value.address_postcode,
        is_group: this.planType == 'group_plan' ? 1 : 0,
        cases: this.caseList,
        caseid: this.isPaymentProposal ? this.caseId : undefined,
        frequency: this.isPaymentProposal ? this.frequency : undefined,
        isGroupArrangement: this.isPaymentProposal ? (this.planType == 'group_plan') : undefined,
        arrangement_id: undefined,
      };

      this.caseService.newEzidebitRecurringAchPayment(this.caseId, recurringPayload).subscribe({
        next: (res: any) => {
          this.saveBtnDisabled = false;
          this.commonService.dismissLoader();
          if (res.data?.success) {
            this.caseService.saveCaseDetails(localStorage.getItem('debtor_id'));
            if (res.data.data?.arrangement_id) {
              recurringPayload.arrangement_id = res.data.data.arrangement_id;
            }
            this.caseService.paymentPlan(recurringPayload).subscribe(() => {});
            this.ionNav.popToRoot();
            this.commonService.showToast(
              this.translate.instant('PAYMENT.ACTION_PERFORMED_SUCCESSFULLY'),
              'success'
            );
          } else {
            this.commonService.showToast(res.data?.message || res.message, 'danger');
          }
        },
        error: () => {
          this.saveBtnDisabled = false;
          this.commonService.dismissLoader();
          this.commonService.showToast(this.translate.instant('COMMON.NETWORK_ERROR'), 'danger');
        },
      });
      return;
    }

    const payload: any = {
      gateway_id: this.responseData.id,
      debtor_id: this.debtorDetails.debtor_id,
      debtor_name: this.achForm.value.debtor_name,
      debtor_first_name: first_name,
      debtor_last_name: last_name || '',
      debtor_email: this.achForm.value.debtor_email,
      account_name: this.achForm.value.account_name,
      bsb: this.achForm.value.bsb,
      account_number: this.achForm.value.account_number,
      address_ln1: this.achForm.value.address_ln1,
      address_postcode: this.achForm.value.address_postcode,
      post_date: this.normalizeDate(this.achForm.value.post_date),
    };

    const paymentRequest = this.isFuturePayment(payload.post_date)
      ? this.caseService.makeEzidebitFutureAchPayment(this.caseId, payload)
      : this.caseService.makeEzidebitAchPayment(this.caseId, payload);

    paymentRequest.subscribe({
      next: (res: any) => {
        this.saveBtnDisabled = false;
        this.commonService.dismissLoader();
        if (res.result) {
          this.commonService.showToast(res.message || this.translate.instant('PAYMENT.ACTION_PERFORMED_SUCCESSFULLY'), 'success');
          this.caseService.saveCaseDetails(localStorage.getItem('debtor_id'));
          this.ionNav.popToRoot();
        } else {
          this.commonService.showToast(res.message, 'danger');
        }
      },
      error: () => {
        this.saveBtnDisabled = false;
        this.commonService.dismissLoader();
        this.commonService.showToast(this.translate.instant('COMMON.NETWORK_ERROR'), 'danger');
      },
    });
  }

  private isFuturePayment(postDate: string): boolean {
    const normalizedPostDate = this.normalizeDate(postDate);
    return !!normalizedPostDate && normalizedPostDate > this.minPostDate;
  }

  private normalizeDate(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    return dateValue.split('T')[0];
  }

  async showAlert(message: string) {
    const alert = await this.alertController.create({
      header: this.translate.instant('COMMON.ALERT'),
      message,
      buttons: ['OK'],
    });
    await alert.present();
  }

  cancel() {
    this.ionNav.pop();
  }
}
